'use client';

import { type FC, useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { useCurrency } from '../../../../hooks/use-currency';
import { useTranslations } from '../../../../hooks/use-translations';

import { SelectForm, type SelectFormProps } from './SelectForm';

type CurrencySelectFormProps = Omit<SelectFormProps<string>, 'options'> & {
  availableCurrency?: boolean;
  withEmpty?: boolean;
};

export const CurrencySelectForm: FC<CurrencySelectFormProps> = (props) => {
  const { name, availableCurrency, placeholder, withEmpty, ...selectProps } = props;

  const SOURCE_CURRENCY_ID_STATE = name ?? 'currency_id';
  const SOURCE_GATE_CURRENCY_ID_STATE = 'gate_currency_id';

  const tMisc = useTranslations('misc');
  const t = useTranslations('dictionaries.currency');

  const { watch, setValue } = useFormContext();
  const { field: controllerField } = useController({ name: SOURCE_CURRENCY_ID_STATE });

  const currency_id = watch(SOURCE_CURRENCY_ID_STATE);

  const { loading, data } = useCurrency(availableCurrency);

  useEffect(() => {
    const { unsubscribe } = watch((value, info) => {
      const isWatchGateCurrencyId = info.name === SOURCE_GATE_CURRENCY_ID_STATE;

      if (!isWatchGateCurrencyId) {
        return;
      }

      const currency_id = value[SOURCE_CURRENCY_ID_STATE];
      const gate_currency_id = value[SOURCE_GATE_CURRENCY_ID_STATE];

      if (!gate_currency_id || currency_id === gate_currency_id) {
        return;
      }

      setValue(SOURCE_CURRENCY_ID_STATE, gate_currency_id);
    });

    return () => unsubscribe();
  }, [SOURCE_CURRENCY_ID_STATE, setValue, watch]);

  const prepareFieldValue = useMemo(() => {
    const isCurrencyData = data?.currency.length;

    if (!isCurrencyData) {
      return '';
    }

    return selectProps.multiple ? (currency_id ?? []) : (currency_id ?? '');
  }, [currency_id, data?.currency.length, selectProps.multiple]);

  return (
    <SelectForm
      {...controllerField}
      value={prepareFieldValue}
      placeholder={loading ? tMisc('loading') : placeholder}
      options={
        data
          ? [
              ...(withEmpty ? [{ value: '', name: t('all') }] : []),
              ...data!.currency.map((cur) => ({ name: cur.id, value: cur.id })),
            ]
          : []
      }
      disabled={loading}
      {...selectProps}
    />
  );
};
